<template>
  <v-container fluid class="app-add-page">
    <div class="app-add-page__inner">
    <!-- Page hero. No surface of its own: the sheet under it is the panel. -->
    <div class="add-hero">
      <button
        type="button"
        class="back-btn"
        aria-label="Back"
        @click="handleBack()"
      >
        <v-icon size="20">mdi-arrow-left</v-icon>
      </button>
      <div class="add-hero__info">
        <span class="add-hero__eyebrow">{{ heroEyebrow }}</span>
        <h1 class="add-hero__title">{{ steps[currentFormPage - 1] }}</h1>
      </div>
      <span class="add-hero__step">
        Step {{ currentFormPage }}<span class="add-hero__step-total">/{{ steps.length }}</span>
      </span>
    </div>

    <!-- Stepper Progress -->
    <div class="stepper">
      <div class="stepper__track">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="stepper__step"
        >
          <div
            class="stepper__circle"
            :class="{
              'stepper__circle--active': currentFormPage === index + 1,
              'stepper__circle--completed': currentFormPage > index + 1,
              'stepper__circle--upcoming': currentFormPage < index + 1,
            }"
          >
            <v-icon v-if="currentFormPage > index + 1" size="16">mdi-check</v-icon>
            <span v-else class="stepper__number">{{ index + 1 }}</span>
          </div>
          <span
            class="stepper__label"
            :class="{
              'stepper__label--active': currentFormPage === index + 1,
              'stepper__label--completed': currentFormPage > index + 1,
            }"
          >{{ step }}</span>
          <div
            v-if="index < steps.length - 1"
            class="stepper__line"
            :class="{ 'stepper__line--completed': currentFormPage > index + 1 }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Page 1: HRDC Account Details -->
    <transition name="slide-fade" mode="out-in">
      <div v-if="currentFormPage == 1" key="page1">
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-office-building-outline</v-icon>
            <span class="section-header__title">HRDC Account Details</span>
          </div>
          <div class="section-body">

            <!-- 1. Company Name -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">1.</span>
                Company Name
                <span class="form-question__note">(As Per SSM)</span>
                <span class="required">*</span>
              </label>
              <span class="form-group__helper">Example: XXX SDN. BHD.</span>
              <v-text-field
                v-model="formData.companyName"
                :error-messages="errorField.companyName"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                class="app-input"
                @input="onInputChange('companyName')"
              ></v-text-field>
            </div>

            <!-- 2. Company SSM Number -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">2.</span>
                Company <b>NEW</b> SSM Number
                <span class="required">*</span>
              </label>
              <span class="form-group__helper">Example: 199701020680, Symbols is not allowed</span>
              <v-text-field
                ref="companySsmNo"
                v-model="formData.companySsmNo"
                :error-messages="errorField.companySsmNo"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                type="number"
                class="app-input"
                @input="onInputChange('companySsmNo')"
              ></v-text-field>
            </div>

            <!-- 3. Company TIN Number -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">3.</span>
                Company TIN Number
                <span class="required">*</span>
              </label>
              <span class="form-group__helper">Example: IG123456789</span>
              <v-text-field
                ref="companyTinNo"
                v-model="formData.companyTinNo"
                :error-messages="errorField.companyTinNo"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                type="text"
                class="app-input"
                @input="onInputChange('companyTinNo')"
              ></v-text-field>
            </div>

            <!-- 4. Company Billing Address -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">4.</span>
                Company Billing Address
                <span class="required">*</span>
              </label>
              <v-text-field
                v-model="formData.companyAddress"
                :error-messages="errorField.companyAddress"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                class="app-input"
                @input="onInputChange('companyAddress')"
              ></v-text-field>
            </div>

            <!-- 5. HRDC PIC Name -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">5.</span>
                HRDC PIC Name
                <span class="required">*</span>
              </label>
              <v-text-field
                v-model="formData.picName"
                :error-messages="errorField.picName"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                class="app-input"
                @input="onInputChange('picName')"
              ></v-text-field>
            </div>

            <!-- 6. HRDC PIC To Email (mandatory, multi-tag) -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">6.</span>
                HRDC PIC To Email
                <span class="required">*</span>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      size="16"
                      class="form-group__info"
                      v-on="on"
                    >mdi-information-outline</v-icon>
                  </template>
                  <span>Press <kbd>Tab</kbd> or <kbd>Enter</kbd> to add multiple emails</span>
                </v-tooltip>
              </label>
              <div
                class="tag-input"
                :class="{ 'tag-input--error': errorField.picEmailTo }"
                @click="focusToInput()"
              >
                <span
                  v-for="(val, i) in formData.picEmailTo"
                  :key="'picEmailTo-' + i"
                  class="tag-input__chip"
                >
                  {{ val }}
                  <button
                    type="button"
                    class="tag-input__chip-remove"
                    @click.stop="removePicEmailTo(i)"
                  >
                    <v-icon size="14" color="var(--app-text-muted)">mdi-close</v-icon>
                  </button>
                </span>
                <input
                  ref="picEmailToInput"
                  v-model="picEmailToInput"
                  type="text"
                  class="tag-input__input"
                  :placeholder="formData.picEmailTo.length === 0 ? 'Enter email address' : ''"
                  @keydown.enter.prevent="addPicEmailToTag()"
                  @keydown.tab.prevent="addPicEmailToTag()"
                  @blur="addPicEmailToTag()"
                />
              </div>
              <div v-if="errorField.picEmailTo" class="tag-input__error">{{ errorField.picEmailTo }}</div>
            </div>

            <!-- 6.1 HRDC PIC Cc Email (optional, multi-tag) -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">6.1</span>
                HRDC PIC Cc Email
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-icon
                      size="16"
                      class="form-group__info"
                      v-on="on"
                    >mdi-information-outline</v-icon>
                  </template>
                  <span>Press <kbd>Tab</kbd> or <kbd>Enter</kbd> to add multiple emails</span>
                </v-tooltip>
              </label>
              <div
                class="tag-input"
                :class="{ 'tag-input--error': errorField.picEmailCc }"
                @click="focusCcInput()"
              >
                <span
                  v-for="(val, i) in formData.picEmailCc"
                  :key="'picEmailCc-' + i"
                  class="tag-input__chip"
                >
                  {{ val }}
                  <button
                    type="button"
                    class="tag-input__chip-remove"
                    @click.stop="removePicEmailCc(i)"
                  >
                    <v-icon size="14" color="var(--app-text-muted)">mdi-close</v-icon>
                  </button>
                </span>
                <input
                  ref="picEmailCcInput"
                  v-model="picEmailCcInput"
                  type="text"
                  class="tag-input__input"
                  :placeholder="formData.picEmailCc.length === 0 ? 'Optional — enter email address' : ''"
                  @keydown.enter.prevent="addPicEmailCcTag()"
                  @keydown.tab.prevent="addPicEmailCcTag()"
                  @blur="addPicEmailCcTag()"
                />
              </div>
              <div v-if="errorField.picEmailCc" class="tag-input__error">{{ errorField.picEmailCc }}</div>
            </div>

            <!-- 7. HRDC PIC Contact Number -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">7.</span>
                HRDC PIC Contact Number
                <span class="required">*</span>
              </label>
              <v-text-field
                ref="picContactNo"
                v-model="formData.picContactNo"
                :error-messages="errorField.picContactNo"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                type="number"
                class="app-input"
                @input="onInputChange('picContactNo')"
              ></v-text-field>
            </div>
          </div>
        </div>

        <!-- Page 1 Navigation -->
        <div class="form-actions">
          <div></div>
          <button
            class="action-btn action-btn--primary"
            @click="changeFormPage(2, true)"
          >
            Next
            <v-icon size="18" class="ml-1">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>
    </transition>

    <!-- Page 2: Claim Details -->
    <transition name="slide-fade" mode="out-in">
      <div v-if="currentFormPage == 2" key="page2">
        <div class="section-card">
          <div class="section-header">
            <v-icon color="white" small>mdi-file-document-edit-outline</v-icon>
            <span class="section-header__title">Claim Details</span>
          </div>
          <div class="section-body">

            <!-- 8. Workshop / Event Session -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">8.</span>
                Workshop / Event Session
                <span class="required">*</span>
              </label>
              <v-select
                :items="eventItems"
                v-model="formData.events"
                :error-messages="errorField.events"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                :loading="apiGetEvents.isLoading"
                class="app-input"
                @input="onInputChange('events')"
                :menu-props="{
                  bottom: true,
                  offsetY: true,
                }"
                @change="changeEventDetails()"
              ></v-select>
            </div>

            <!-- 9. Other Attachment -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">9.</span>
                Other Attachment (if any)
              </label>
              <div
                class="file-upload-area"
                :class="{
                  'file-upload-area--error': errorField.otherAttachment,
                  'file-upload-area--drag': dragOver.otherAttachment,
                }"
                @click="$refs.otherAttachmentInput.click()"
                @dragover.prevent="dragOver.otherAttachment = true"
                @dragleave.prevent="dragOver.otherAttachment = false"
                @drop.prevent="handleFileDrop($event, 'otherAttachment')"
              >
                <input
                  ref="otherAttachmentInput"
                  type="file"
                  accept="image/*,.pdf"
                  style="display: none"
                  @change="handleFileSelect($event, 'otherAttachment')"
                />
                <div v-if="!formData.otherAttachment" class="file-upload-area__placeholder">
                  <v-icon size="28" color="var(--app-text-faint)">mdi-cloud-upload-outline</v-icon>
                  <span class="file-upload-area__text">Click or drag file to upload</span>
                  <span class="file-upload-area__hint">Supports: Images, PDF</span>
                </div>
                <div v-else class="file-upload-area__selected">
                  <v-icon size="20" color="var(--app-accent-strong)">mdi-file-check-outline</v-icon>
                  <span class="file-upload-area__filename">{{ formData.otherAttachment.name }}</span>
                  <v-icon
                    size="18"
                    color="var(--app-text-faint)"
                    class="file-upload-area__remove"
                    @click.stop="clearFile('otherAttachment')"
                  >mdi-close-circle</v-icon>
                </div>
              </div>
              <div v-if="errorField.otherAttachment" class="file-upload-error">{{ errorField.otherAttachment }}</div>
              <span class="form-group__helper">Please rename your file name before upload.</span>
            </div>

            <!-- 10. Number of Pax -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">10.</span>
                Number of Pax
                <span class="required">*</span>
              </label>
              <v-text-field
                v-model="formData.noOfPax"
                :error-messages="errorField.noOfPax"
                outlined
                dense
                placeholder="The value must be a number"
                hide-details="auto"
                type="number"
                class="app-input"
                @input="onInputChange('noOfPax')"
              ></v-text-field>
            </div>

            <!-- Participant Details -->
            <div
              v-for="(pax, index) in Array.from(
                { length: Number(formData.noOfPax) },
                (_, i) => i + 1
              )"
              :key="index"
              class="participant-block"
            >
              <div class="participant-block__badge">Participant {{ pax }}</div>

              <div class="form-group">
                <label class="form-group__label">
                  Full Name (As Per IC)
                  <span class="required">*</span>
                </label>
                <v-text-field
                  ref="participantName"
                  :error-messages="errorField.participantName[index]"
                  outlined
                  dense
                  placeholder="Enter your answer"
                  hide-details="auto"
                  class="app-input"
                ></v-text-field>
              </div>

              <div class="form-group">
                <label class="form-group__label">
                  IC Number
                  <span class="required">*</span>
                </label>
                <span class="form-group__helper">Only number without any symbol. (etc. 990101881234)</span>
                <v-text-field
                  ref="participantNric"
                  :error-messages="errorField.participantNric[index]"
                  outlined
                  dense
                  placeholder="Enter your answer"
                  hide-details="auto"
                  type="number"
                  class="app-input"
                  @input="onInputDigits(index, 'participantNric')"
                ></v-text-field>
              </div>

              <div class="form-group">
                <label class="form-group__label">
                  Email
                  <span class="required">*</span>
                </label>
                <v-text-field
                  ref="participantEmail"
                  :error-messages="errorField.participantEmail[index]"
                  outlined
                  dense
                  placeholder="Enter your answer"
                  hide-details="auto"
                  class="app-input"
                ></v-text-field>
              </div>
            </div>

            <!-- 11. Payment Made -->
            <div class="form-group">
              <label class="form-group__label">
                <span class="form-question__number">11.</span>
                Payment Made
                <span class="required">*</span>
              </label>
              <v-select
                :items="isPaymentMadeItems"
                v-model="formData.isPaymentMade"
                :error-messages="errorField.isPaymentMade"
                outlined
                dense
                placeholder="Enter your answer"
                hide-details="auto"
                class="app-input"
                @input="onInputChange('isPaymentMade')"
                item-text="label"
                :menu-props="{
                  bottom: true,
                  offsetY: true,
                }"
              >
                <template v-slot:item="data">
                  <div v-html="data.item.text"></div>
                </template>
              </v-select>
            </div>

            <!-- No Payment Made Section -->
            <div v-if="formData.isPaymentMade == false">
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">12.</span>
                  Claim Amount (RM)
                  <span class="required">*</span>
                </label>
                <v-select
                  :items="pricingItems"
                  v-model="formData.paymentNoMadeClaimAmount"
                  :error-messages="errorField.paymentNoMadeClaimAmount"
                  outlined
                  dense
                  placeholder="Select claim amount"
                  hide-details="auto"
                  class="app-input"
                  @input="onInputChange('paymentNoMadeClaimAmount')"
                  @change="onSelectChange('paymentNoMadeClaimAmount')"
                  :menu-props="pricingMenuProps"
                ></v-select>
                <v-text-field
                  v-if="viewOtherPaymentNoMadeClaimAmount"
                  v-model="formData.otherPaymentNoMadeClaimAmount"
                  :error-messages="errorField.otherPaymentNoMadeClaimAmount"
                  outlined
                  dense
                  placeholder="Enter amount (numbers only)"
                  hide-details="auto"
                  type="number"
                  class="app-input mt-2"
                  @input="onOtherAmountInput('otherPaymentNoMadeClaimAmount')"
                ></v-text-field>
              </div>
            </div>

            <!-- Payment Made Section -->
            <div v-if="formData.isPaymentMade == true">
              <!-- 13. Bank Account Holder Name -->
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">13.</span>
                  Bank Account Holder Name
                  <span class="required">*</span>
                </label>
                <v-text-field
                  v-model="formData.bankAccHolderName"
                  :error-messages="errorField.bankAccHolderName"
                  outlined
                  dense
                  placeholder="Enter your answer"
                  hide-details="auto"
                  class="app-input"
                  @input="onInputChange('bankAccHolderName')"
                ></v-text-field>
              </div>

              <!-- 14. Name of Bank -->
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">14.</span>
                  Name of Bank
                  <span class="form-question__note">(for HRDC refund purpose)</span>
                  <span class="required">*</span>
                </label>
                <v-autocomplete
                  :items="bankItems"
                  v-model="formData.bank"
                  :error-messages="errorField.bank"
                  outlined
                  dense
                  placeholder="Select your answer"
                  hide-details="auto"
                  :loading="apiGetBanks.isLoading"
                  class="app-input"
                  @input="onInputChange('bank')"
                  @change="onSelectChange('bank')"
                  :menu-props="{
                    bottom: true,
                    offsetY: true,
                  }"
                ></v-autocomplete>
                <v-text-field
                  v-if="viewOtherBank"
                  v-model="formData.otherBank"
                  :error-messages="errorField.otherBank"
                  outlined
                  dense
                  placeholder="Other"
                  hide-details="auto"
                  class="app-input mt-2"
                  @input="onInputChange('otherBank')"
                ></v-text-field>
              </div>

              <!-- 14. Bank Account Number -->
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">14.</span>
                  Bank Account Number
                  <span class="form-question__note">(for HRDC refund purpose)</span>
                  <span class="required">*</span>
                </label>
                <span class="form-group__helper">Only number without any symbol.</span>
                <v-text-field
                  ref="bankAccNo"
                  v-model="formData.bankAccNo"
                  :error-messages="errorField.bankAccNo"
                  outlined
                  dense
                  placeholder="Enter your answer"
                  hide-details="auto"
                  type="number"
                  class="app-input"
                  @input="onInputChange('bankAccNo')"
                ></v-text-field>
              </div>

              <!-- 15. Bank Statement Screenshot -->
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">15.</span>
                  Bank Statement Header / Screenshot of Bank Account Details for Refund
                  <span class="required">*</span>
                </label>
                <div
                  class="file-upload-area"
                  :class="{
                    'file-upload-area--error': errorField.ssRefundBankAccAtch,
                    'file-upload-area--drag': dragOver.ssRefundBankAccAtch,
                  }"
                  @click="$refs.ssRefundBankAccAtchInput.click()"
                  @dragover.prevent="dragOver.ssRefundBankAccAtch = true"
                  @dragleave.prevent="dragOver.ssRefundBankAccAtch = false"
                  @drop.prevent="handleFileDrop($event, 'ssRefundBankAccAtch')"
                >
                  <input
                    ref="ssRefundBankAccAtchInput"
                    type="file"
                    accept="image/*,.pdf"
                    style="display: none"
                    @change="handleFileSelect($event, 'ssRefundBankAccAtch')"
                  />
                  <div v-if="!formData.ssRefundBankAccAtch" class="file-upload-area__placeholder">
                    <v-icon size="28" color="var(--app-text-faint)">mdi-cloud-upload-outline</v-icon>
                    <span class="file-upload-area__text">Click or drag file to upload</span>
                    <span class="file-upload-area__hint">Supports: Images, PDF</span>
                  </div>
                  <div v-else class="file-upload-area__selected">
                    <v-icon size="20" color="var(--app-accent-strong)">mdi-file-check-outline</v-icon>
                    <span class="file-upload-area__filename">{{ formData.ssRefundBankAccAtch.name }}</span>
                    <v-icon
                      size="18"
                      color="var(--app-text-faint)"
                      class="file-upload-area__remove"
                      @click.stop="clearFile('ssRefundBankAccAtch')"
                    >mdi-close-circle</v-icon>
                  </div>
                </div>
                <div v-if="errorField.ssRefundBankAccAtch" class="file-upload-error">{{ errorField.ssRefundBankAccAtch }}</div>
              </div>

              <!-- 16. Payment Method -->
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">16.</span>
                  Payment Method
                  <span class="required">*</span>
                </label>
                <div class="section-description section-description--compact">
                  <span class="section-description__title">Bank Transfer:</span>
                  <span>Company Name: YYC GST CONSULTANTS SDN BHD - 201301043188 (1073010-M)</span>
                  <span>Bank Name: CIMB Bank</span>
                  <span>Bank Account Number: 8603863136</span>
                </div>
                <v-select
                  :items="paymentMethodItems"
                  v-model="formData.paymentMethod"
                  :error-messages="errorField.paymentMethod"
                  outlined
                  dense
                  placeholder="Select your answer"
                  hide-details="auto"
                  class="app-input"
                  @input="onInputChange('paymentMethod')"
                  @change="onSelectChange('paymentMethod')"
                  :menu-props="{
                    bottom: true,
                    offsetY: true,
                  }"
                ></v-select>
                <v-text-field
                  v-if="viewOtherPaymentMethod"
                  v-model="formData.otherPaymentMethod"
                  :error-messages="errorField.otherPaymentMethod"
                  outlined
                  dense
                  placeholder="Other"
                  hide-details="auto"
                  class="app-input mt-2"
                  @input="onInputChange('otherPaymentMethod')"
                ></v-text-field>
              </div>

              <!-- 17. Payment Amount -->
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">17.</span>
                  Payment Amount (RM)
                  <span class="required">*</span>
                </label>
                <v-select
                  :items="pricingItems"
                  v-model="formData.paymentAmount"
                  :error-messages="errorField.paymentAmount"
                  outlined
                  dense
                  placeholder="Select payment amount"
                  hide-details="auto"
                  class="app-input"
                  @input="onInputChange('paymentAmount')"
                  @change="onSelectChange('paymentAmount')"
                  :menu-props="pricingMenuProps"
                ></v-select>
                <v-text-field
                  v-if="viewOtherPaymentAmount"
                  v-model="formData.otherPaymentAmount"
                  :error-messages="errorField.otherPaymentAmount"
                  outlined
                  dense
                  placeholder="Enter amount (numbers only)"
                  hide-details="auto"
                  type="number"
                  class="app-input mt-2"
                  @input="onOtherAmountInput('otherPaymentAmount')"
                ></v-text-field>
              </div>

              <!-- 18. Claim Amount -->
              <div class="form-group">
                <label class="form-group__label">
                  <span class="form-question__number">18.</span>
                  Claim Amount (RM)
                  <span class="required">*</span>
                </label>
                <v-select
                  :items="pricingItems"
                  v-model="formData.paymentMadeClaimAmount"
                  :error-messages="errorField.paymentMadeClaimAmount"
                  outlined
                  dense
                  placeholder="Select claim amount"
                  hide-details="auto"
                  class="app-input"
                  @input="onInputChange('paymentMadeClaimAmount')"
                  @change="onSelectChange('paymentMadeClaimAmount')"
                  :menu-props="pricingMenuProps"
                ></v-select>
                <v-text-field
                  v-if="viewOtherPaymentMadeClaimAmount"
                  v-model="formData.otherPaymentMadeClaimAmount"
                  :error-messages="errorField.otherPaymentMadeClaimAmount"
                  outlined
                  dense
                  placeholder="Enter amount (numbers only)"
                  hide-details="auto"
                  type="number"
                  class="app-input mt-2"
                  @input="onOtherAmountInput('otherPaymentMadeClaimAmount')"
                ></v-text-field>
              </div>
            </div>
          </div>
        </div>

        <!-- Page 2 Navigation -->
        <div class="form-actions">
          <button
            class="action-btn action-btn--secondary"
            @click="changeFormPage(1)"
          >
            <v-icon size="18" class="mr-1">mdi-arrow-left</v-icon>
            Previous
          </button>
          <button
            class="action-btn action-btn--submit"
            @click="changeFormPage('submit', true)"
          >
            <v-icon size="18" class="mr-1">mdi-check-circle-outline</v-icon>
            Submit
          </button>
        </div>
      </div>
    </transition>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
    </div>
  </v-container>
</template>

<script>
import { mapState } from "vuex";
import Api from "@/objects/api";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc_event/objects/globalData";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,

    }),
    currentFormProgress() {
      return (this.currentFormPage / this.totalFormPage) * 100;
    },
    // name1 is the event module's short name ("E-Invoicing"), assigned in
    // created() from the registry — so it is already there on first render.
    // The fallback only covers a serviceKey with no config behind it.
    heroEyebrow() {
      return this.name1 ? `New ${this.name1} Application` : "New Application";
    },
    // The stepper, the hero title and the "Step n/N" pill all read from this
    // one list, so a page is never labelled in two places.
    steps() {
      return ["HRDC Account Details", "Claim Details"];
    },
    // The module's catalogue price list, as v-select items. `staticPricing` is
    // assigned in created() by the mixin's serviceDataAssignation() from the
    // EventModule row for this service — shape `{ member: [], nonMember: [] }`,
    // with a legacy flat array still in the wild (settings/event_modules reads
    // it the same way).
    //
    // The two tiers are flattened into ONE list: the form asks what was paid,
    // not which tier the payer is in, and the same figure can appear in both
    // tiers on some modules. De-duplicated because a v-select holding the same
    // value twice highlights both rows when either is picked, and sorted
    // ascending because with the tiers gone the source order carries no
    // meaning.
    pricingItems() {
      const pricing = this.staticPricing || {};
      const legacy = Array.isArray(pricing);
      const prices = legacy
        ? pricing
        : [...(pricing.member || []), ...(pricing.nonMember || [])];

      const unique = [...new Set(prices.map((price) => Number(price)))]
        .filter((price) => !isNaN(price))
        .sort((a, b) => a - b);

      const items = unique.map((price) => ({
        text: `RM ${price.toLocaleString()}`,
        value: price,
      }));

      // Always offered: a module can price a one-off differently from its
      // catalogue, and a blank amount field is not an option on a claim form.
      if (items.length > 0) items.push({ divider: true });
      items.push({ text: "Other", value: "Other" });

      return items;
    },

    // Menus are detached to [data-app], outside this page's scope id — the
    // content class is how the subheader rule below reaches them.
    pricingMenuProps() {
      return {
        bottom: true,
        offsetY: true,
        contentClass: "pricing-menu",
      };
    },
  },
  props: ["params"],
  data: () => ({
    apiGetEvents: new Api(),
    apiGetBanks: new Api(),
    apiSubmit: new Api(),
    hrdcData: new HrdcData(),
    service: new Service(),
    model: new Model(),
    formColor: "#135875",
    exampleColor: "#616161",
    currentFormPage: 1,
    totalFormPage: 2,
    loadingDialog: false,
    eventItems: [],
    bankItems: [],
    isPaymentMadeItems: [],
    page1: {
      companyName: null,
      companySsmNo: null,
      companyTinNo: null,
      companyAddress: null,
      picName: null,
      picContactNo: null,
    },
    page2: {
      events: null,
      noOfPax: null,
      isPaymentMade: null,
      paymentNoMadeClaimAmount: null,
      bankAccHolderName: null,
      bank: null,
      otherBank: null,
      bankAccNo: null,
      ssRefundBankAccAtch: null,
      paymentMethod: null,
      otherPaymentMethod: null,
      paymentAmount: null,
      paymentMadeClaimAmount: null,
      otherAttachment: null,
    },
    page2PaymentMadeItem: {
      events: null,
      noOfPax: null,
      isPaymentMade: null,
      bankAccHolderName: null,
      bank: null,
      otherBank: null,
      bankAccNo: null,
      ssRefundBankAccAtch: null,
      paymentMethod: null,
      otherPaymentMethod: null,
      paymentAmount: null,
      paymentMadeClaimAmount: null,
      otherAttachment: null,
    },
    page2PaymentNoMadeItem: {
      events: null,
      noOfPax: null,
      isPaymentMade: null,
      paymentNoMadeClaimAmount: null,
      otherAttachment: null,
    },
    formData: {
      companyName: null,
      companySsmNo: null,
      companyTinNo: null,
      companyAddress: null,
      picName: null,
      picEmailTo: [],
      picEmailCc: [],
      picContactNo: null,
      events: null,
      noOfPax: null,
      isPaymentMade: null,
      paymentNoMadeClaimAmount: null,
      bankAccHolderName: null,
      bank: null,
      otherBank: null,
      bankAccNo: null,
      ssRefundBankAccAtch: null,
      paymentMethod: null,
      otherPaymentMethod: null,
      paymentAmount: null,
      paymentMadeClaimAmount: null,
      otherAttachment: null,
      // Free-typed amounts. Deliberately NOT members of the page2* maps: those
      // maps drive both validation AND the submit payload, and the backend
      // must keep receiving exactly paymentAmount / *ClaimAmount. They are
      // validated by hand in changeFormPage() and substituted in submit()
      // through containsOtherOptions, the same way otherBank already is.
      otherPaymentNoMadeClaimAmount: null,
      otherPaymentAmount: null,
      otherPaymentMadeClaimAmount: null,
    },
    errorField: {
      companyName: null,
      companySsmNo: null,
      companyTinNo: null,
      companyAddress: null,
      picName: null,
      picEmailTo: null,
      picEmailCc: null,
      picContactNo: null,
      events: null,
      noOfPax: null,
      participantName: [],
      participantNric: [],
      participantEmail: [],
      isPaymentMade: null,
      paymentNoMadeClaimAmount: null,
      bankAccHolderName: null,
      bank: null,
      otherBank: null,
      bankAccNo: null,
      ssRefundBankAccAtch: null,
      paymentMethod: null,
      otherPaymentMethod: null,
      paymentAmount: null,
      paymentMadeClaimAmount: null,
      otherAttachment: null,
      otherPaymentNoMadeClaimAmount: null,
      otherPaymentAmount: null,
      otherPaymentMadeClaimAmount: null,
    },
    picEmailToInput: '',
    picEmailCcInput: '',
    isSubmitted: {
      page1: false,
      page2: false,
    },
    containsOtherOptions: {
      bank: "otherBank",
      paymentMethod: "otherPaymentMethod",
      paymentNoMadeClaimAmount: "otherPaymentNoMadeClaimAmount",
      paymentAmount: "otherPaymentAmount",
      paymentMadeClaimAmount: "otherPaymentMadeClaimAmount",
    },
    viewOtherPaymentMethod: false,
    viewOtherBank: false,
    viewOtherPaymentNoMadeClaimAmount: false,
    viewOtherPaymentAmount: false,
    viewOtherPaymentMadeClaimAmount: false,
    dragOver: {
      otherAttachment: false,
      ssRefundBankAccAtch: false,
    },
    fieldsNotRequiredValidate: ["otherAttachment"],
  }),
  watch: {
    "formData.noOfPax"(newValue) {
      if (newValue > 9 || newValue <= 0) {
        this.$nextTick(() => {
          this.formData.noOfPax = null;
        });
      }
    },
  },
  created() {
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) {
      this.service.set("key", this.model.serviceKey);
    }

    // Events Assignation.
    this.serviceDataAssignation(this.model.serviceKey);

    const apiDetails = {
      apiGetEvents: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/event/get-events?active=true&event_type=${
          this.hrdcType
        }`,
        method: "get",
      },
      apiGetBanks: {
        url: `${this.$service[this.service.key]}/v1/en/console/bank/get-banks`,
        method: "get",
      },
      apiSubmit: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/create-application`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.apiGetEvents.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (!status || data.length == 0) {
          this.errorField.events =
            "Unavailable to retrieve events, please contact admin for support";
        }

        if (status) {
          for (const item of data) {
            this.eventItems.push({
              text: this.formatEventTitle(item, this.hrdcType),
              value: JSON.stringify({
                uuid: item.uuid,
                title: item.title,
                eventCategory: item.eventCategory,
                isFullPaymentMadeAllow: item.isFullPaymentMadeAllow,
                isNoPaymentMadeAllow: item.isNoPaymentMadeAllow,
              }),
            });
          }
        }
      } catch (err) {
        console.log(err);
        this.errorField.events =
          "Unavailable to retrieve events, please contact admin for support";
      }
    });
    this.apiGetEvents.fetch();

    this.apiGetBanks.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;
        if (!status || data.length == 0) {
          this.errorField.bank =
            "Unavailable to retrieve banks, please contact admin for support";
        }

        if (status) {
          for (const item of data) {
            const [, ...bankNamePart] = item.bankType.split("-");
            const bankName = bankNamePart.join("-").trim();

            this.bankItems.push({
              text: bankName,
              value: bankName,
            });
          }

          // Push 'Others' option.
          this.bankItems.push({
            text: "Other",
            value: "Other",
          });
        }
      } catch (err) {
        console.log(err);
        this.errorField.bank =
          "Unavailable to retrieve banks, please contact admin for support";
      }
    });
    this.apiGetBanks.fetch();
  },
  mounted() {
    // A focused <input type="number"> steps its value on mouse wheel. The
    // spinner arrows are hidden in CSS, but that behaviour survives, so
    // scrolling the form right after typing silently edited the field the
    // user had just filled in (799 -> 798 -> 797). Drop focus on the first
    // wheel tick: the event's default action is applied after propagation
    // and only to a still-focused input, so the typed value is kept and the
    // page scrolls normally.
    this.numberWheelGuard = (event) => {
      const el = event.target;
      if (
        el instanceof HTMLInputElement &&
        el.type === "number" &&
        el === document.activeElement
      ) {
        el.blur();
      }
    };
    this.$el.addEventListener("wheel", this.numberWheelGuard, { passive: true });
  },
  beforeDestroy() {
    if (this.numberWheelGuard) {
      this.$el.removeEventListener("wheel", this.numberWheelGuard);
    }
  },
  methods: {
    redirectBack() {
      this.$store.commit("assignDeveloperData", {
        paginationPageNo: this.$route.query.currentPageNo,
      });

      this.$router.push({
        name: this.eventRoute("Application"),
        params: {
          modelKey: this.model.key,
        },
        query: {
          viewId: "all",
          _ausk: this.$route.query._ausk,
        },
      });
    },
    // The hero's back arrow walks the wizard backwards first and only leaves
    // the page from step 1 — the same affordance as Previous, without asking
    // the user to hunt for it at the bottom of a long form.
    handleBack() {
      if (this.currentFormPage > 1) {
        this.changeFormPage(this.currentFormPage - 1);
        return;
      }
      this.redirectBack();
    },
    handleFileSelect(event, field) {
      const file = event.target.files[0];
      if (file) {
        this.formData[field] = file;
        this.onInputChange(field);
      }
      // Clear the native input so re-picking the SAME file still fires change.
      event.target.value = "";
    },
    handleFileDrop(event, field) {
      this.dragOver[field] = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.formData[field] = file;
        this.onInputChange(field);
      }
    },
    clearFile(field) {
      this.formData[field] = null;
      this.onInputChange(field);
    },
    changeFormPage(pageNo, toNextPage = false) {
      // Check is to next page or back.
      let pass = false;
      let page2ItemToValidate = null;
      if (toNextPage) {
        // Mark the page as submitted.
        switch (this.currentFormPage) {
          case 1:
            this.isSubmitted.page1 = true;
            // Auto-commit any text the user typed but did not press Enter/Tab on
            if ((this.picEmailToInput || '').trim()) this.addPicEmailToTag();
            if ((this.picEmailCcInput || '').trim()) this.addPicEmailCcTag();
            pass = this.validateData(this.page1);
            if (!this.formData.picEmailTo || this.formData.picEmailTo.length === 0) {
              this.errorField.picEmailTo = "At least one To email is required";
              pass = false;
            }
            break;
          case 2:
            this.isSubmitted.page2 = true;

            // Check for No. Of Pax.
            if (this.formData.noOfPax) {
              const participantValidateFields = [
                "participantName",
                "participantNric",
                "participantEmail",
              ];
              for (const field of participantValidateFields) {
                this.validateParticipantDetails(field);
              }
            }

            // If payment made.
            if (this.formData.isPaymentMade) {
              page2ItemToValidate = this.page2PaymentMadeItem;

              const othersValidation = {
                viewOtherBank: "otherBank",
                viewOtherPaymentMethod: "otherPaymentMethod",
              };

              for (const otherItem in othersValidation) {
                if (!this[otherItem]) {
                  delete page2ItemToValidate[othersValidation[otherItem]];
                } else {
                  page2ItemToValidate[othersValidation[otherItem]] =
                    this.page2PaymentMadeItem[othersValidation[otherItem]];
                }
              }
            }

            if (!this.formData.isPaymentMade) {
              page2ItemToValidate = this.page2PaymentNoMadeItem;
            }

            pass = this.validateData(page2ItemToValidate);

            if (!this.validateOtherAmounts()) {
              pass = false;
            }
            break;
        }
      }

      if (pass || !toNextPage) {
        // Clear error messages.
        this.clearErrorMsg(this[`page${this.currentFormPage}`]);

        if (pageNo == "submit") {
          this.submit();
          return;
        }

        this.currentFormPage = pageNo;
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    // The three amount selects fall back to a typed number under "Other". Only
    // the fields on the branch the user is actually filling in are checked —
    // switching No Payment -> Full Payment must not leave the hidden
    // no-payment "Other" box blocking Submit.
    validateOtherAmounts() {
      const onThisBranch = this.formData.isPaymentMade
        ? {
            viewOtherPaymentAmount: "otherPaymentAmount",
            viewOtherPaymentMadeClaimAmount: "otherPaymentMadeClaimAmount",
          }
        : {
            viewOtherPaymentNoMadeClaimAmount: "otherPaymentNoMadeClaimAmount",
          };

      let pass = true;
      for (const flag in onThisBranch) {
        const field = onThisBranch[flag];
        if (!this[flag]) {
          this.errorField[field] = null;
          continue;
        }

        const value = this.formData[field];
        if (value === null || value === "" || value === undefined) {
          this.errorField[field] = "This question is required.";
          pass = false;
          continue;
        }

        this.errorField[field] = null;
      }

      return pass;
    },
    onOtherAmountInput(field) {
      if (!this.isSubmitted.page2) return;
      this.errorField[field] =
        this.formData[field] === null || this.formData[field] === ""
          ? "This question is required."
          : null;
    },
    validateParticipantDetails(field) {
      this.errorField[field] = [];
      for (let i = 0; i < this.formData.noOfPax; i++) {
        if (
          this.$refs[field][i].lazyValue == null ||
          this.$refs[field][i].lazyValue == "" ||
          this.$refs[field][i].lazyValue == undefined
        ) {
          this.errorField[field][i] = "This field is required";
        } else {
          this.errorField[field][i] = null;
        }
      }
    },
    validateData(items) {
      let pass = true;
      for (const item in items) {
        // If items is not required checking, pass it.
        if (this.fieldsNotRequiredValidate.includes(item)) {
          continue;
        }

        if (this.formData[item] === null || this.formData[item] === "") {
          this.errorField[item] = "This question is required.";
          pass = false;
        }
      }
      return pass;
    },
    isValidEmail(s) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    },
    focusToInput() {
      this.$nextTick(() => {
        if (this.$refs.picEmailToInput) this.$refs.picEmailToInput.focus();
      });
    },
    focusCcInput() {
      this.$nextTick(() => {
        if (this.$refs.picEmailCcInput) this.$refs.picEmailCcInput.focus();
      });
    },
    addPicEmailToTag() {
      const val = (this.picEmailToInput || '').trim();
      if (!val) return;
      if (!this.isValidEmail(val)) {
        this.errorField.picEmailTo = `"${val}" is not a valid email address`;
        return;
      }
      if (this.formData.picEmailTo.indexOf(val) === -1) {
        this.formData.picEmailTo.push(val);
      }
      this.picEmailToInput = '';
      this.errorField.picEmailTo = null;
    },
    removePicEmailTo(index) {
      this.formData.picEmailTo.splice(index, 1);
    },
    addPicEmailCcTag() {
      const val = (this.picEmailCcInput || '').trim();
      if (!val) return;
      if (!this.isValidEmail(val)) {
        this.errorField.picEmailCc = `"${val}" is not a valid email address`;
        return;
      }
      if (this.formData.picEmailCc.indexOf(val) === -1) {
        this.formData.picEmailCc.push(val);
      }
      this.picEmailCcInput = '';
      this.errorField.picEmailCc = null;
    },
    removePicEmailCc(index) {
      this.formData.picEmailCc.splice(index, 1);
    },
    onInputChange(item) {
      // Only check when user clicked to next page.
      let pageToCheck = null;
      const pages = [this.page1, this.page2];
      pages.forEach((page, index) => {
        if (item in page) {
          pageToCheck = index + 1;
        }
      });

      if (this.isSubmitted[`page${pageToCheck}`]) {
        // Optional fields never carry a "required" message. The old
        // v-file-input fired this on clear too, so emptying the optional
        // attachment after a failed Next painted a required error under a
        // field nothing asks for — now that the dropzone prints that error at
        // full width, it is impossible to miss.
        const optional = this.fieldsNotRequiredValidate.includes(item);

        if (!optional && (this.formData[item] === null || this.formData[item] === "")) {
          this.errorField[item] = "The question is required";
          return false;
        }

        this.errorField[item] = null;
      }

      // Check for only digits inputs.
      const onlyDigitFields = ["bankAccNo", "picContactNo", "companySsmNo"];
      if (onlyDigitFields.includes(item)) {
        this.onInputDigits(null, item);
      }
    },
    onInputDigits(index = null, field) {
      let input = null;
      if (index || index == 0) {
        input = this.$refs[field][index];
      } else {
        input = this.$refs[field];
      }
      input.$el.querySelector("input").value = input.$el
        .querySelector("input")
        .value.replace(/[^0-9]/g, "");
    },
    onSelectChange(item) {
      const viewMap = {
        bank: "viewOtherBank",
        paymentMethod: "viewOtherPaymentMethod",
        paymentNoMadeClaimAmount: "viewOtherPaymentNoMadeClaimAmount",
        paymentAmount: "viewOtherPaymentAmount",
        paymentMadeClaimAmount: "viewOtherPaymentMadeClaimAmount",
      };

      if (!viewMap[item]) return;

      if (this.formData[item] == "Other") {
        this[viewMap[item]] = true;
      } else {
        this[viewMap[item]] = false;
        // Picking a catalogue price retires whatever the box was complaining
        // about; the field itself is gone from the DOM.
        const companion = this.containsOtherOptions[item];
        if (companion) this.errorField[companion] = null;
      }
    },
    showErrorMessage(response) {
      for (const key in response.data) {
        this.errorField[key] = null;
        this.errorField[key] = response.data[key];
      }
    },
    clearErrorMsg(errors) {
      for (let error in errors) {
        this.errorField[error] = null;
      }
    },
    changeEventDetails() {
      this.isPaymentMadeItems = [];

      const eventObj = JSON.parse(this.formData.events);
      const { isFullPaymentMadeAllow, isNoPaymentMadeAllow } = eventObj;

      if (isFullPaymentMadeAllow) {
        this.isPaymentMadeItems.push({
          label: "Full Payment",
          text: "Full Payment",
          value: true,
        });
      }

      if (isNoPaymentMadeAllow) {
        this.isPaymentMadeItems.push({
          label: "No Payment",
          text: "No Payment <span style='color: red;'>(Please attach approval proof in Other Attachment before submitting application.)</span>",
          value: false,
        });
      }
    },
    submit() {
      this.showLoadingDialog();

      // Construct base data.
      const formData = new FormData();
      formData.append("userUuid", this.auth.uuid);
      formData.append("hrdcType", this.hrdcType);

      // Append event participants.
      const participantName = {};
      const participantNric = {};
      const participantEmail = {};
      for (let i = 0; i < this.formData.noOfPax; i++) {
        participantName[i] = this.$refs.participantName[i].lazyValue;
        participantNric[i] = this.$refs.participantNric[i].lazyValue;
        participantEmail[i] = this.$refs.participantEmail[i].lazyValue;
      }

      formData.append("participantName", JSON.stringify(participantName));
      formData.append("participantNric", JSON.stringify(participantNric));
      formData.append("participantEmail", JSON.stringify(participantEmail));

      for (const page1Item in this.page1) {
        formData.append(page1Item, this.formData[page1Item]);
      }

      // PIC To/Cc emails are arrays — send as JSON to keep ordering and
      // distinguish empty from a single-string with a comma.
      formData.append(
        "picEmailTo",
        JSON.stringify(this.formData.picEmailTo || [])
      );
      formData.append(
        "picEmailCc",
        JSON.stringify(this.formData.picEmailCc || [])
      );

      // If payment made.
      if (this.formData.isPaymentMade) {
        for (const page2PaymentMadeItem in this.page2PaymentMadeItem) {
          // Check if data contains 'Others' option.
          if (
            this.formData[page2PaymentMadeItem] === "Other" &&
            page2PaymentMadeItem in this.containsOtherOptions
          ) {
            formData.append(
              page2PaymentMadeItem,
              this.formData[this.containsOtherOptions[page2PaymentMadeItem]]
            );

            continue;
          }

          formData.append(
            page2PaymentMadeItem,
            this.formData[page2PaymentMadeItem]
          );
        }
      }

      // If payment no made.
      if (!this.formData.isPaymentMade) {
        for (const page2PaymentNoMadeItem in this.page2PaymentNoMadeItem) {
          // Same "Other" substitution as the payment-made branch above: the
          // typed amount is sent under the ORIGINAL key, so the payload the
          // backend sees is unchanged by the select.
          if (
            this.formData[page2PaymentNoMadeItem] === "Other" &&
            page2PaymentNoMadeItem in this.containsOtherOptions
          ) {
            formData.append(
              page2PaymentNoMadeItem,
              this.formData[this.containsOtherOptions[page2PaymentNoMadeItem]]
            );

            continue;
          }

          formData.append(
            page2PaymentNoMadeItem,
            this.formData[page2PaymentNoMadeItem]
          );
        }
      }

      this.apiSubmit.setParams(formData);
      this.apiSubmit.setCallbackCompleted((response) => {
        try {
          if (!response.status) {
            this.showErrorMessage(response);
            if (response.message) {
              this.$store.dispatch("showMessage", {
                message: response.message,
                messageType: "error",
                timeout: 2000,
              });
            }

            this.hideLoadingDialog();
            return false;
          }

          if (response.status) {
            this.hideLoadingDialog();

            this.$store.commit("assignDeveloperData", {
              paginationPageNo: this.$route.query.currentPageNo,
            });

            this.$router.push({
              name: this.eventRoute("ViewApplication"),
              query: {
                viewId: this.$store.state[this.service.key].data.viewId,
                _ausk: response.application.uuid,
              },
            });
            this.$store.dispatch("showMessage", {
              message: response.data,
              messageType: "success",
              timeout: 2000,
            });
          }
        } catch (err) {
          console.log(err);
        }
      });
      this.apiSubmit.fetch();
    },
  },
};
</script>

<style scoped>
/* ============================================================================
   HRDC EVENT — ADD APPLICATION
   ----------------------------------------------------------------------------
   Styled to match src/services/hrdc/views/application/Add.vue. Same reasoning,
   same rules, so the two wizards stay one design when either is edited.

   THIS PAGE IS A SHEET, NOT A STACK OF SLABS.

   The root is now the routed <v-container>, so src/plugins/theme-glass.css §2
   hands it the frosted panel, the rim, the lift and the WELD to the breadcrumb
   above — the shell every console page gets, from one place. That is what the
   old markup duplicated by hand: an #eaecef header slab, an #eaecef stepper
   slab and #eaecef cards, three tones of the same grey floating inside a panel
   that already floats.

   So everything down to the first .section-card paints CONTENT ON the sheet —
   the hero, the stepper rail and the action bar carry no surface of their own
   — and SURFACES are left to the glass layer. .section-card, .section-body,
   .section-header and .back-btn are styled globally there at (1,1,0), which
   outranks every scoped rule in this file; restating their background/radius/
   shadow here would be dead CSS that reads as if it were in effect. What is
   left below is only what that layer cannot know: the light header band (its
   icons are `color="white"` → .white--text !important, so a stylesheet that
   blanks the band would leave white glyphs on white), this page's own widgets,
   and spacing.

   NO LITERAL COLOURS. Every value is a var(--app-*) resolved by theme-dark.css,
   which is what keeps the two themes in step from one edit — the dark half of
   this file is four rules now instead of the ~60 it used to carry across two
   overlapping auto-generated blocks.
   ============================================================================ */

/* ============================================
   PAGE SHEET
   ============================================ */
.app-add-page {
  /* Fills main minus the breadcrumb and footer. A `100vh - 77px` guess only
     subtracts the app bar and overflows — see .layout-main__content. */
  flex: 1 0 auto;
  padding: 28px 24px 56px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* The sheet spans the whole column like every other page; the FORM inside it
   keeps a readable measure instead of stretching one input across a 1600px
   monitor. Centring lives here, not on the sheet, because the sheet's width and
   margins are owned by theme-glass. */
.app-add-page__inner {
  width: 100%;
  max-width: 940px;
  margin: 0 auto;
}

/* ============================================
   HERO
   ============================================ */
.add-hero {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 20px;
}

/* Surface, rim, radius and hover come from theme-glass §5 — only the box the
   button needs is set here. */
.back-btn {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-muted);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.back-btn:hover {
  color: var(--app-accent);
}

/* The icons below carry no `color` prop, so they inherit the colour of the
   button or circle around them and follow its hover/active state for free.
   Vuetify's own `.theme--light.v-icon` is (0,2,0); these are (0,2,0) plus the
   scope attribute, so they win without !important. */
.back-btn .v-icon,
.stepper__circle .v-icon,
.action-btn .v-icon {
  color: inherit;
}

.add-hero__info {
  flex: 1;
  min-width: 0;
}

/* The wizard's identity moves to the eyebrow so the h1 can be the STEP. The
   old title repeated "New Application - " on both pages, which meant the only
   part that changed sat at the far end of an ellipsised line. */
.add-hero__eyebrow {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: var(--app-text-faint);
  margin-bottom: 3px;
}

.add-hero__title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
  letter-spacing: -0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.add-hero__step {
  display: inline-flex;
  align-items: baseline;
  padding: 6px 14px;
  border-radius: 999px;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
  color: var(--app-accent-strong);
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

.add-hero__step-total {
  opacity: 0.6;
  font-weight: 600;
}

/* ============================================
   STEPPER — a rail on the sheet, not a card
   ============================================ */
.stepper {
  padding-bottom: 22px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--app-border);
}

.stepper__track {
  display: flex;
  align-items: center;
  justify-content: center;
}

.stepper__step {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.stepper__circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

/* Only the CURRENT step is a solid fill. A completed step used to be a second
   saturated disc, so page 2 showed two loud circles and no answer to "where am
   I"; it is a soft tile now and the ring does the pointing. */
.stepper__circle--active {
  background: var(--app-accent);
  color: var(--app-accent-fg);
  box-shadow: 0 0 0 4px var(--app-ring);
}

.stepper__circle--completed {
  background: var(--app-success-bg);
  border-color: var(--app-success-border);
  color: var(--app-success-fg);
}

.stepper__circle--upcoming {
  background: var(--app-form-tile);
  border-color: var(--app-border);
  color: var(--app-text-faint);
}

.stepper__number {
  font-size: 0.9rem;
  font-weight: 700;
}

.stepper__label {
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--app-text-faint);
  white-space: nowrap;
  transition: color 0.3s ease;
}

.stepper__label--active {
  color: var(--app-accent-strong);
  font-weight: 700;
}

.stepper__label--completed {
  color: var(--app-success-fg);
}

.stepper__line {
  width: 40px;
  height: 2px;
  border-radius: 2px;
  background: var(--app-border);
  margin: 0 8px;
  transition: background 0.3s ease;
  flex-shrink: 0;
}

.stepper__line--completed {
  background: var(--app-success);
}

/* ============================================
   SECTION CARDS
   --------------------------------------------
   Surface / rim / radius / lift / hover: theme-glass §4. Only the light band's
   paint and the interior spacing are this page's business.
   ============================================ */
.section-card {
  margin-bottom: 24px;
}

/* --app-brand-gradient, not a hand-rolled sky gradient: it is the token built
   for a saturated header in BOTH themes, and its -fg pairs with the white
   icons the template pins. Dark never sees it — theme-glass §4 neutralises the
   band app-wide from `html.dark-mode #app` (an id, so it wins). */
.section-header {
  background: var(--app-brand-gradient);
  padding: 15px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-header__title {
  color: var(--app-brand-gradient-fg);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
}

.section-body {
  padding: 26px 24px;
}

.section-description {
  font-size: 0.94rem;
  color: var(--app-text-muted);
  line-height: 1.6;
  margin-bottom: 22px;
  padding: 13px 18px;
  background: var(--app-accent-soft);
  border: 1px solid var(--app-accent-border);
  border-left: 3px solid var(--app-accent);
  border-radius: var(--glass-radius-sm);
}

/* The bank-transfer block under "Payment Method" is a stack of short lines, not
   a paragraph — it sits directly above the field it explains. */
.section-description--compact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.88rem;
  margin-bottom: 12px;
}

.section-description__title {
  font-weight: 700;
  color: var(--app-text);
}

/* ============================================
   FORM GROUPS
   ============================================ */
.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group__label {
  display: block;
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--app-text);
  margin-bottom: 7px;
  line-height: 1.5;
}

.required {
  color: var(--app-danger);
  font-weight: 700;
}

.form-group__info {
  margin-left: 4px;
  color: var(--app-text-faint) !important;
  cursor: help;
  vertical-align: -2px;
  transition: color 0.15s;
}

.form-group__info:hover {
  color: var(--app-accent) !important;
}

.form-group__helper {
  display: block;
  font-size: 0.8rem;
  color: var(--app-text-faint);
  margin-top: 5px;
  margin-bottom: 7px;
  line-height: 1.4;
}

/* This form numbers its questions; the number is the one accent in the label,
   so it scans as a list rather than as a wall of bold text. */
.form-question__number {
  font-weight: 700;
  color: var(--app-accent-strong);
  margin-right: 5px;
}

.form-question__note {
  font-weight: 600;
  font-style: italic;
  color: var(--app-text-muted);
}

/* ============================================
   PARTICIPANT BLOCK (multi-pax)
   ============================================ */
.participant-block {
  border-top: 1px solid var(--app-border);
  padding-top: 22px;
  margin-top: 22px;
  margin-bottom: 22px;
}

.participant-block__badge {
  display: inline-block;
  background: var(--app-info-bg);
  color: var(--app-info-fg);
  font-size: 0.76rem;
  font-weight: 700;
  padding: 5px 13px;
  border-radius: 999px;
  margin-bottom: 18px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

/* ============================================
   FILE UPLOAD AREA
   --------------------------------------------
   Same family as theme-glass's .file-card: a tile ONE step in from the card,
   so it takes the form-tile tier and no shadow — a dropzone that floats reads
   as a second card, not as a slot in this one.
   ============================================ */
.file-upload-area {
  border: 1px dashed var(--app-field-border);
  border-radius: var(--glass-radius-sm);
  padding: 24px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  background: var(--app-form-tile);
  text-align: center;
}

.file-upload-area:hover,
.file-upload-area--drag {
  border-color: var(--app-accent);
  background: var(--app-accent-soft);
}

.file-upload-area--error,
.file-upload-area--error:hover {
  border-color: var(--app-danger);
}

.file-upload-area__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
}

.file-upload-area__text {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--app-text-muted);
}

.file-upload-area__hint {
  font-size: 0.8rem;
  color: var(--app-text-faint);
}

.file-upload-area__selected {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}

.file-upload-area__filename {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--app-accent-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;
}

.file-upload-area__remove {
  cursor: pointer;
  transition: color 0.15s ease;
}

.file-upload-area__remove:hover {
  color: var(--app-danger) !important;
}

.file-upload-error {
  font-size: 0.8rem;
  color: var(--app-danger);
  margin-top: 5px;
}

/* ============================================
   ACTION BAR
   ============================================ */
.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding-top: 20px;
  border-top: 1px solid var(--app-border);
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 30px;
  border-radius: var(--glass-radius-sm);
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  font-family: inherit;
}

.action-btn--primary {
  background: var(--app-accent);
  color: var(--app-accent-fg);
}

.action-btn--primary:hover {
  box-shadow: 0 4px 14px var(--app-accent-glow);
  transform: translateY(-1px);
}

.action-btn--secondary {
  background: var(--app-form-tile);
  color: var(--app-text-muted);
  border-color: var(--app-border-strong);
}

.action-btn--secondary:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-field-border-hover);
  color: var(--app-text);
}

/* Submit is the page's one terminal action, so it takes the brand gradient
   rather than a second flat accent fill — Next and Submit were near-identical
   blues sitting in the same corner of consecutive pages. */
.action-btn--submit {
  background: var(--app-brand-gradient);
  color: var(--app-brand-gradient-fg);
}

.action-btn--submit:hover {
  background: var(--app-brand-gradient-hover);
  box-shadow: 0 4px 14px var(--app-brand-glow);
  transform: translateY(-1px);
}

/* ============================================
   TAG INPUT (To / Cc emails)
   ============================================ */
.tag-input {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--app-field-border);
  border-radius: var(--glass-radius-sm);
  background: var(--app-field);
  cursor: text;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
}

.tag-input:hover {
  border-color: var(--app-field-border-hover);
}

/* A RING on focus, not a 2px border with the padding shaved by 1px to
   compensate: the old pair reflowed every chip in the box by a pixel on focus
   and again on blur. */
.tag-input:focus-within {
  border-color: var(--app-accent);
  box-shadow: 0 0 0 3px var(--app-ring);
}

.tag-input--error,
.tag-input--error:hover {
  border-color: var(--app-danger);
}

.tag-input--error:focus-within {
  border-color: var(--app-danger);
  box-shadow: 0 0 0 3px var(--app-ring-danger);
}

.tag-input__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--app-info-bg);
  color: var(--app-info-fg);
  border: 1px solid var(--app-accent-border);
  border-radius: 8px;
  padding: 3px 6px 3px 10px;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.4;
}

.tag-input__chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s;
}

.tag-input__chip-remove:hover {
  background: var(--app-hover);
}

.tag-input__input {
  border: none;
  outline: none;
  background: transparent;
  flex: 1;
  min-width: 140px;
  padding: 4px 0;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--app-text);
  font-family: inherit;
}

.tag-input__input::placeholder {
  color: var(--app-field-placeholder);
}

.tag-input__error {
  margin-top: 6px;
  font-size: 0.8rem;
  color: var(--app-danger);
  line-height: 1.3;
}

kbd {
  display: inline-block;
  padding: 1px 5px;
  font-family: 'SF Mono', Monaco, Consolas, monospace;
  font-size: 0.72rem;
  color: var(--app-neutral-fg);
  background: var(--app-neutral-bg);
  border: 1px solid var(--app-border-strong);
  border-radius: 4px;
}

/* ============================================
   DARK
   --------------------------------------------
   Everything above is token-driven, so dark needs only what a token cannot
   express: the section band, whose light paint is a saturated gradient. The
   band itself is already neutralised app-wide by theme-glass §4
   (`html.dark-mode #app .section-header` — an id, so it beats these rules on
   background). What is left is putting the accent back at CUE size rather than
   as a field, matching hrdc/views/application/Add.vue, View.vue and Edit.vue.
   ============================================ */
.dark-mode .section-header {
  position: relative;
}

/* 2px and faded top-to-bottom: it marks where the header starts without
   drawing a hard full-height rail. .section-card's overflow clips it to the
   card radius. */
.dark-mode .section-header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(
    180deg,
    rgba(var(--app-accent-rgb), 0.55),
    rgba(var(--app-accent-rgb), 0.12)
  );
}

.dark-mode .section-header__title {
  color: var(--app-text);
  letter-spacing: 1.6px;
}

/* The icon can hold the accent now that the band behind it is neutral.
   `color="white"` on the tag compiles to .white--text (0,1,0 + !important);
   this is (0,3,0 + !important) and wins. */
.dark-mode .section-header .v-icon {
  color: var(--app-accent) !important;
  opacity: 0.85;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter {
  transform: translateX(24px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-24px);
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 959px) {
  .app-add-page {
    padding: 20px 16px 44px !important;
  }

  .add-hero__title {
    font-size: 1.12rem;
  }

  .stepper {
    overflow-x: auto;
  }

  .stepper__track {
    min-width: 460px;
  }

  .stepper__label {
    font-size: 0.8rem;
  }

  .stepper__line {
    width: 26px;
  }

  .section-header {
    padding: 14px 18px;
  }

  .section-body {
    padding: 22px 18px;
  }
}

@media (max-width: 599px) {
  .app-add-page {
    padding: 16px 12px 36px !important;
  }

  .add-hero {
    gap: 12px;
  }

  .add-hero__step {
    display: none;
  }

  .stepper__track {
    min-width: 400px;
  }

  .stepper__circle {
    width: 30px;
    height: 30px;
  }

  .stepper__number {
    font-size: 0.82rem;
  }

  .form-actions {
    gap: 10px;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
    padding: 12px 16px;
    font-size: 0.92rem;
  }
}
</style>

<style>
/* ============================================================================
   UNSCOPED: Vuetify input chrome for this page only.
   ----------------------------------------------------------------------------
   Unscoped because a few of the nodes below (the <fieldset>, the message list)
   are rendered by Vuetify outside this component's scope id. `.app-add-page`
   on every selector is what keeps the rules on this page — the file used to
   pair each rule with a `.dark-mode` twin; one token-driven rule now serves
   both themes.
   ============================================================================ */

.app-add-page .app-input.v-text-field--outlined > .v-input__control > .v-input__slot {
  border-radius: var(--glass-radius-sm) !important;
  min-height: 48px;
}

/* Vuetify draws the outlined border on a <fieldset> that is absolutely
   positioned 5px ABOVE .v-input__slot (top: -5px, to make room for the
   floating-label notch), so filling the SLOT leaves that 5px band under the top
   border unpainted and the card colour shows through as a gap — invisible while
   --app-field and --app-card were both #fff, obvious the moment the field tier
   differs from the card. Fill the fieldset instead: it owns the border box.
   Then lift the field content above it, since an absolutely positioned fieldset
   paints over its static siblings. */
.app-add-page .app-input.v-text-field--outlined fieldset {
  background: var(--app-field);
  border-color: var(--app-field-border) !important;
  border-radius: var(--glass-radius-sm) !important;
  transition: border-color 0.15s ease;
}

.app-add-page .app-input .v-input__slot > *:not(fieldset) {
  position: relative;
  z-index: 1;
}

.app-add-page .app-input.v-text-field--outlined:hover fieldset {
  border-color: var(--app-field-border-hover) !important;
}

.app-add-page .app-input.v-input--is-focused fieldset {
  border-color: var(--app-accent) !important;
  border-width: 2px !important;
}

.app-add-page .app-input .v-text-field__slot input,
.app-add-page .app-input .v-text-field__slot textarea,
.app-add-page .app-input.v-select .v-select__selection,
.app-add-page .app-input.v-autocomplete .v-select__selection {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.98rem !important;
  font-weight: 600 !important;
  color: var(--app-text) !important;
}

.app-add-page .app-input .v-text-field__slot input::placeholder,
.app-add-page .app-input .v-text-field__slot textarea::placeholder {
  color: var(--app-field-placeholder) !important;
  font-weight: 500 !important;
}

.app-add-page .app-input .v-label {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.98rem !important;
  color: var(--app-field-placeholder) !important;
}

/* The v-select chevron takes the theme's default ink, which on the dark field
   is nearly the field's own colour. */
.app-add-page .app-input .v-input__append-inner .v-icon {
  color: var(--app-text-faint) !important;
}

/* Error state */
.app-add-page .app-input.v-input--has-state fieldset,
.app-add-page .app-input.v-input--has-state:hover fieldset {
  border-color: var(--app-danger) !important;
}

.app-add-page .app-input .v-messages__message {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.8rem !important;
  color: var(--app-danger) !important;
}

/* ============================================
   PRICING MENU (the amount selects)
   --------------------------------------------
   v-menu content is detached to [data-app], so it is outside BOTH this
   component's scope id and .app-add-page — `contentClass: "pricing-menu"` on
   the menu props is the only handle that reaches it. The panel, its rim and
   the list background all come from theme-glass §3; what is set here is
   DENSITY.

   The list is a handful of short rows — four prices and "Other" on the busiest
   module — and Vuetify's stock 48px rows spread that over ~240px of menu for
   maybe 40px of text. Selectors carry .v-menu__content so they land at (0,3,0)
   and beat Vuetify's own `.v-select-list .v-list-item` on specificity rather
   than on injection order, which a lazily-routed chunk cannot rely on.
   ============================================ */
.pricing-menu.v-menu__content .v-list-item {
  min-height: 32px;
  padding: 0 14px;
}

.pricing-menu.v-menu__content .v-list-item__title {
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.3;
}

.pricing-menu.v-menu__content .v-list-item__content {
  padding: 4px 0;
}

.pricing-menu.v-menu__content .v-divider {
  margin: 3px 0;
}

/* The list's own vertical padding is the last of the stock 48px-row spacing
   left once the rows are tightened. */
.pricing-menu.v-menu__content .v-list {
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Number fields here are quantities and IDs, never sliders — the spinners only
   offer a way to nudge an SSM number by one. Hidden in both engines; the wheel
   guard in mounted() covers the scroll half of the same problem. */
.app-add-page input::-webkit-outer-spin-button,
.app-add-page input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.app-add-page input[type="number"] {
  -moz-appearance: textfield;
}
</style>
